import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FileDown, Play, Sparkles, BarChart3, Printer } from "lucide-react";
import { toast } from "sonner";
import { BRL, fmtDate } from "@/lib/format";

const AGG_OPTIONS = [
  { key: "count", label: "Contagem" },
  { key: "sum:salario", label: "Soma de salários" },
  { key: "avg:salario", label: "Salário médio" },
  { key: "min:salario", label: "Salário mínimo" },
  { key: "max:salario", label: "Salário máximo" },
];

const DATE_COLS = new Set(["data_admissao", "data_desligamento", "data_inicio", "data_fim"]);
const MONEY_COLS = new Set(["salario", "sum_salario", "avg_salario", "min_salario", "max_salario"]);

export default function ReportBuilderPage() {
  const [entities, setEntities] = useState([]);
  const [ent, setEnt] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState({});
  const [groupBy, setGroupBy] = useState("");
  const [aggs, setAggs] = useState(["count"]);
  const [sortBy, setSortBy] = useState("");
  const [sortDesc, setSortDesc] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/reports/builder/entities").then(({ data }) => {
      setEntities(data);
      if (data.length && !ent) setEnt(data[0].key);
    });
    // eslint-disable-next-line
  }, []);

  const entityMeta = entities.find(e => e.key === ent);

  useEffect(() => {
    if (entityMeta) {
      setSelectedFields(Object.keys(entityMeta.fields).slice(0, 6));
      setFilters({});
      setGroupBy("");
      setSortBy("");
      setResult(null);
    }
    // eslint-disable-next-line
  }, [ent]);

  const runReport = async () => {
    if (!ent) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/reports/builder/run", {
        entity: ent, fields: selectedFields, filters,
        group_by: groupBy || null, aggregations: aggs,
        sort_by: sortBy || null, sort_desc: sortDesc, limit: 1000,
      });
      setResult(data);
      toast.success(`Relatório gerado · ${data.count} registros`);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Falha ao gerar relatório");
    } finally { setLoading(false); }
  };

  const exportExcel = async () => {
    if (!ent) return;
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/reports/builder/export-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          entity: ent, fields: selectedFields, filters,
          group_by: groupBy || null, aggregations: aggs,
          sort_by: sortBy || null, sort_desc: sortDesc, limit: 1000,
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `relatorio_${ent}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Falha ao exportar"); }
  };

  const toggleField = (k) => {
    setSelectedFields(sf => sf.includes(k) ? sf.filter(x => x !== k) : [...sf, k]);
  };
  const toggleAgg = (k) => setAggs(a => a.includes(k) ? a.filter(x => x !== k) : [...a, k]);

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">
          Inteligência · Construtor de relatórios
        </div>
        <h1 className="font-display text-3xl font-semibold">Builder de Relatórios</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          Monte relatórios customizados escolhendo entidade, colunas, filtros, agrupamentos e agregações.
        </p>
      </div>

      {/* Builder panel */}
      <div className="rounded-lg border border-border bg-white p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Entidade</Label>
            <Select value={ent} onValueChange={setEnt}>
              <SelectTrigger data-testid="rb-entity"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {entities.map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Agrupar por (opcional)</Label>
            <Select value={groupBy} onValueChange={(v) => setGroupBy(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="rb-groupby"><SelectValue placeholder="Sem agrupamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sem agrupamento</SelectItem>
                {(entityMeta?.groupable || []).map(g => (
                  <SelectItem key={g} value={g}>{entityMeta?.fields?.[g] || g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Ordenar por</Label>
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v === "__none__" ? "" : v)}>
                <SelectTrigger className="flex-1" data-testid="rb-sortby"><SelectValue placeholder="Sem ordenação" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem ordenação</SelectItem>
                  {Object.keys(entityMeta?.fields || {}).map(f => (
                    <SelectItem key={f} value={f}>{entityMeta?.fields[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => setSortDesc(!sortDesc)}
                      title={sortDesc ? "Descendente" : "Ascendente"} className="shrink-0">
                {sortDesc ? "↓" : "↑"}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider mb-2 block">Colunas do relatório</Label>
          <div className="flex flex-wrap gap-2" data-testid="rb-fields">
            {Object.entries(entityMeta?.fields || {}).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => toggleField(k)}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                  selectedFields.includes(k)
                    ? "bg-[#234935] border-[#234935] text-white"
                    : "bg-white border-border hover:border-[#C5A059]"
                }`}
              >{label}</button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider mb-1 block">Filtros</Label>
          <div className="text-[11px] text-muted-foreground mb-2">
            Busca por <b>trecho</b> do valor, ignorando maiúsculas e acentos. Ex.: <code className="bg-slate-100 px-1 rounded">tecnico</code> encontra "TÉCNICO" e "Tecnico Pleno".
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(entityMeta?.filterable || []).map(k => {
              const label = entityMeta?.fields?.[k] || k;
              const isId = k.endsWith("_id");
              return (
                <div key={k}>
                  <Label className="text-[10.5px] capitalize text-muted-foreground">{label}{isId ? " (id exato)" : ""}</Label>
                  <Input
                    value={filters[k] || ""}
                    onChange={(e) => setFilters({ ...filters, [k]: e.target.value || undefined })}
                    placeholder={isId ? "UUID" : `ex.: ${k === "status" ? "ativo" : k === "cargo" ? "tecnico" : "filtrar..."}`}
                    data-testid={`rb-filter-${k}`}
                  />
                </div>
              );
            })}
          </div>
          {Object.values(filters).some(v => v) && (
            <button
              type="button"
              onClick={() => setFilters({})}
              data-testid="rb-clear-filters"
              className="mt-3 text-[11.5px] text-[#234935] hover:text-[#C5A059] underline underline-offset-2 transition-colors"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {groupBy && (
          <div>
            <Label className="text-[11px] uppercase tracking-wider mb-2 block">Agregações</Label>
            <div className="flex flex-wrap gap-2">
              {AGG_OPTIONS.map(a => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => toggleAgg(a.key)}
                  className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                    aggs.includes(a.key)
                      ? "bg-[#C5A059] border-[#C5A059] text-[#1A231F] font-medium"
                      : "bg-white border-border"
                  }`}
                >{a.label}</button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-border">
          <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={runReport}
                  disabled={loading || !selectedFields.length} data-testid="rb-run-btn">
            <Play className="h-4 w-4 mr-2" /> {loading ? "Gerando..." : "Gerar relatório"}
          </Button>
          {result && (
            <>
              <Button variant="outline" onClick={exportExcel} data-testid="rb-export-btn">
                <FileDown className="h-3.5 w-3.5 mr-1.5" /> Exportar Excel
              </Button>
              <Button variant="outline" onClick={() => window.print()} data-testid="rb-print-btn">
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir
              </Button>
              <Badge variant="outline" className="ml-auto">
                <Sparkles className="h-3 w-3 mr-1 text-[#C5A059]" />
                {result.count} registros
              </Badge>
            </>
          )}
        </div>
      </div>

      {/* Aggregated result */}
      {result?.aggregated && result.aggregated.length > 0 && (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#8A6B2B]" />
            <h3 className="font-display text-[15px] font-semibold">Resumo agregado · {result.label} agrupado por {entityMeta?.fields?.[result.group_by]}</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                {Object.keys(result.aggregated[0]).map(k => (
                  <TableHead key={k} className="text-right first:text-left">{formatAggKey(k, entityMeta)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.aggregated.map((row, i) => (
                <TableRow key={`${row[result.group_by]}-${i}`}>
                  {Object.keys(row).map((k, j) => (
                    <TableCell key={k} className={j === 0 ? "font-medium" : "text-right font-mono tabular-nums"}>
                      {formatCell(k, row[k])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Raw rows */}
      {result?.rows && result.rows.length > 0 && (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-secondary/40">
            <h3 className="font-display text-[15px] font-semibold">Dados detalhados ({result.count})</h3>
          </div>
          <div className="overflow-x-auto max-h-[520px]">
            <Table>
              <TableHeader>
                <TableRow>
                  {selectedFields.map(f => (
                    <TableHead key={f}>{entityMeta?.fields?.[f] || f}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.rows.slice(0, 300).map((r, i) => (
                  <TableRow key={`${r.id || r.matricula || ""}-${i}`}>
                    {selectedFields.map(f => (
                      <TableCell key={f} className={MONEY_COLS.has(f) ? "font-mono text-right" : ""}>
                        {formatCell(f, r[f])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {result.rows.length > 300 && (
              <div className="text-center p-3 text-[12px] text-muted-foreground">
                Mostrando 300 de {result.rows.length} · Use Excel para visualizar completo.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatAggKey(k, entityMeta) {
  if (k === "count") return "Qtde";
  if (k.startsWith("sum_")) return `Σ ${entityMeta?.fields?.[k.slice(4)] || k.slice(4)}`;
  if (k.startsWith("avg_")) return `Média ${entityMeta?.fields?.[k.slice(4)] || k.slice(4)}`;
  if (k.startsWith("min_")) return `Min ${entityMeta?.fields?.[k.slice(4)] || k.slice(4)}`;
  if (k.startsWith("max_")) return `Max ${entityMeta?.fields?.[k.slice(4)] || k.slice(4)}`;
  return entityMeta?.fields?.[k] || k;
}

function formatCell(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (MONEY_COLS.has(key)) return BRL(value);
  if (DATE_COLS.has(key)) return fmtDate(value) || value;
  return String(value);
}
